import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Spin,
  Table,
  Typography,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import SpotSupplierAckPanel from './SpotSupplierAckPanel';
import api from '@/services/api.service';

const { Title, Text, Paragraph } = Typography;

type PortalLine = {
  pr_line_id: string;
  line_number: number;
  item_code: string | null;
  item_description: string;
  uom: string | null;
  max_qty: number;
  existing_qty: number | null;
  existing_unit_price: number | null;
  existing_offer_id: string | null;
  notes: string | null;
  supplier_reference: string | null;
  has_attachment: boolean;
};

type PortalPayload = {
  company_name: string | null;
  pr_number: string;
  currency: string;
  supplier: { id: string; supplier_name: string; supplier_code?: string };
  expires_at: string;
  acknowledged_at?: string | null;
  acknowledged_by_name?: string | null;
  has_acknowledgement?: boolean;
  lines: PortalLine[];
};

export default function SupplierSpotPurchasePortalPage() {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<PortalPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [offerIdsByLine, setOfferIdsByLine] = useState<Record<string, string>>({});
  const [form] = Form.useForm();

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      setLoading(false);
      return;
    }
    api
      .get(`/procurement/public/spot-purchase/${token}`)
      .then((res) => {
        const d = (res?.data?.data ?? res?.data) as PortalPayload;
        setData(d);
        const initial: Record<string, unknown> = {};
        const ids: Record<string, string> = {};
        for (const line of d.lines || []) {
          initial[`qty_${line.pr_line_id}`] = line.existing_qty ?? line.max_qty;
          initial[`price_${line.pr_line_id}`] = line.existing_unit_price ?? undefined;
          initial[`ref_${line.pr_line_id}`] = line.supplier_reference ?? undefined;
          initial[`notes_${line.pr_line_id}`] = line.notes ?? undefined;
          if (line.existing_offer_id) ids[line.pr_line_id] = line.existing_offer_id;
        }
        form.setFieldsValue(initial);
        setOfferIdsByLine(ids);
      })
      .catch((e: any) => {
        setError(e?.response?.data?.message || 'Invalid or expired link');
      })
      .finally(() => setLoading(false));
  }, [token, form]);

  const expiresLabel = useMemo(() => {
    if (!data?.expires_at) return '';
    return dayjs(data.expires_at).format('DD MMM YYYY HH:mm');
  }, [data?.expires_at]);

  const onFinish = async (values: Record<string, any>) => {
    if (!token || !data?.lines?.length) return;
    setSubmitting(true);
    try {
      const lines = data.lines.map((line) => ({
        pr_line_id: line.pr_line_id,
        qty: Number(values[`qty_${line.pr_line_id}`]),
        unit_price: Number(values[`price_${line.pr_line_id}`]),
        currency: data.currency || 'AED',
        supplier_reference: values[`ref_${line.pr_line_id}`] || undefined,
        notes: values[`notes_${line.pr_line_id}`] || undefined,
      }));
      const res = await api.post(`/procurement/public/spot-purchase/${token}/offers`, { lines });
      const payload = res?.data?.data ?? res?.data;
      const offers = (payload?.offers || []) as Array<{ id: string; pr_line_id: string }>;
      const nextIds = { ...offerIdsByLine };
      for (const o of offers) {
        if (o?.id && o?.pr_line_id) nextIds[o.pr_line_id] = o.id;
      }
      setOfferIdsByLine(nextIds);

      for (const line of data.lines) {
        const fileList = values[`file_${line.pr_line_id}`] as
          | Array<{ originFileObj?: File }>
          | undefined;
        const file = fileList?.[0]?.originFileObj;
        const offerId = nextIds[line.pr_line_id];
        if (file && offerId) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('offer_id', offerId);
          await api.post(`/procurement/public/spot-purchase/${token}/documents`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }

      message.success('Quote submitted. The purchaser will review and create the purchase order.');
      setDone(true);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Spin style={{ display: 'block', margin: '48px auto' }} />;
  }
  if (error || !data) {
    return (
      <Alert
        type="error"
        message={error || 'Not found'}
        style={{ maxWidth: 560, margin: '48px auto' }}
      />
    );
  }
  if (done) {
    return (
      <div style={{ maxWidth: 640, margin: '48px auto', padding: 16, textAlign: 'center' }}>
        <Title level={4}>Thank you</Title>
        <Paragraph>
          Your quantities, prices, and documents were saved for{' '}
          <Text strong>{data.pr_number}</Text>. The purchaser will review and submit the purchase
          order.
        </Paragraph>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      <Card>
        <Title level={4} style={{ marginBottom: 4 }}>
          Market purchase quote
        </Title>
        {data.company_name ? (
          <Text type="secondary">{data.company_name}</Text>
        ) : null}
        <Paragraph style={{ marginTop: 12, marginBottom: 4 }}>
          <Text strong>PR:</Text> {data.pr_number}
        </Paragraph>
        <Paragraph style={{ marginBottom: 4 }}>
          <Text strong>Supplier:</Text> {data.supplier?.supplier_name}
          {data.supplier?.supplier_code ? ` (${data.supplier.supplier_code})` : ''}
        </Paragraph>
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Link expires {expiresLabel}. Enter qty and unit price for each line. Optional: attach a
          quote/receipt (PDF or image, max 10MB).
        </Paragraph>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Table
            size="small"
            rowKey="pr_line_id"
            dataSource={data.lines}
            pagination={false}
            scroll={{ x: 900 }}
            columns={[
              {
                title: '#',
                dataIndex: 'line_number',
                width: 48,
              },
              {
                title: 'Item',
                width: 280,
                render: (_: unknown, r: PortalLine) => (
                  <div>
                    <div>{r.item_code ? `${r.item_code} — ` : ''}{r.item_description}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Max {r.max_qty} {r.uom || ''}
                      {r.has_attachment ? ' · Doc attached' : ''}
                    </Text>
                  </div>
                ),
              },
              {
                title: 'Qty',
                width: 110,
                render: (_: unknown, r: PortalLine) => (
                  <Form.Item
                    name={`qty_${r.pr_line_id}`}
                    rules={[{ required: true, message: 'Required' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber min={0.0001} max={r.max_qty} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
              {
                title: `Unit price (${data.currency || 'AED'})`,
                width: 140,
                render: (_: unknown, r: PortalLine) => (
                  <Form.Item
                    name={`price_${r.pr_line_id}`}
                    rules={[{ required: true, message: 'Required' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                ),
              },
              {
                title: 'Your ref',
                width: 140,
                render: (_: unknown, r: PortalLine) => (
                  <Form.Item name={`ref_${r.pr_line_id}`} style={{ marginBottom: 0 }}>
                    <Input placeholder="Optional" />
                  </Form.Item>
                ),
              },
              {
                title: 'Attach',
                width: 140,
                render: (_: unknown, r: PortalLine) => (
                  <Form.Item
                    name={`file_${r.pr_line_id}`}
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    style={{ marginBottom: 0 }}
                  >
                    <Upload beforeUpload={() => false} maxCount={1} accept=".pdf,image/*">
                      <Button icon={<UploadOutlined />} size="small">
                        File
                      </Button>
                    </Upload>
                  </Form.Item>
                ),
              },
            ]}
          />

          <Form.Item style={{ marginTop: 16 }}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Submit quote to purchaser
            </Button>
          </Form.Item>
        </Form>

        {token ? (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <SpotSupplierAckPanel
              token={token}
              defaultName={data.supplier?.supplier_name}
              alreadyAcknowledged={Boolean(data.has_acknowledgement)}
              acknowledgedByName={data.acknowledged_by_name}
            />
          </div>
        ) : null}
      </Card>
    </div>
  );
}
